import { Box, Divider, Stack } from '@mui/material'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { isEinwertValuation } from 'ClientPortal/src/api/valuationModels'
import type { ExistingAsset } from '@einwert/valuation-api-client'
import {
    AreaValue,
    assembleAssetTitle,
    isNotNullOrUndefined,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    Map,
    Marker,
    monetaryToLocalizedString,
    RoundImage,
    tokens,
    TrendIcon,
    Typography,
} from 'ComponentLibrary'
import { getMapsApiKey } from 'ComponentLibrary/src/common/environment'
import { ValueRow, ValueRows } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/Preview/ValueRows'
import {
    RatingRow,
    RatingRows,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/Preview/RatingRows'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type AssetPreviewProps = {
    asset: ExistingAsset
    valuation: ExistingValuation
}

export const AssetPreview = ({ asset, valuation }: AssetPreviewProps) => {
    const locale = useCurrentResolvedLocale()
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tAssetPreview } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationMarketData.assetPreview',
    })
    const { customer_asset_reference, address } = asset
    const title = assembleAssetTitle({ customer_asset_reference, ...address })
    const addressLineOne = joinDefinedStrings([address.street, address.house_number])
    const addressLineTwo = joinDefinedStrings([address.zip, address.city])
    const coordinates = address.coordinates && {
        lat: address.coordinates.latitude,
        lng: address.coordinates.longitude,
    }

    const assetPreviewInfoRows: ValueRow[] = [
        { label: tCommon('marketValue'), value: monetaryToLocalizedString(valuation.market_value, true, true, locale) },
        { label: tCommon('valuationDate'), value: isoDateStringToLocalFormat(valuation.valuation_date, false, locale) },
        {
            label: tCommon('marketRent'),
            value: monetaryToLocalizedString(valuation.tenant_summary?.total?.gross_rental_income.total, true, true, locale),
        },
        {
            label: tCommon('valuePerSquareMeter'),
            value:
                valuation.market_value_per_rentable_area &&
                `${monetaryToLocalizedString(valuation.market_value_per_rentable_area, true, undefined, locale)}/m²`,
        },
        {
            label: tCommon('landArea'),
            value: valuation.land_area && AreaValue.fromDataObject(valuation.land_area).format({ locale }),
        },
        {
            label: tCommon('rentableArea'),
            value: valuation.rentable_area && AreaValue.fromDataObject(valuation.rentable_area).format({ locale }),
        },
    ]

    const ratingPreviewInfoRows: RatingRow[] = [
        { label: tAssetPreview('ratings.locationQuality'), type: 'location', rating: valuation.rating_summary?.location },
        { label: tAssetPreview('ratings.assetQuality'), type: 'asset', rating: valuation.rating_summary?.structures },
        {
            label: tAssetPreview('ratings.rentalSituation'),
            type: 'rent',
            rating: isEinwertValuation(valuation) ? valuation.summary_risks?.rent_situation : valuation.rating_summary?.rent_situation,
        },
    ]

    return (
        <Stack
            direction="column"
            gap={tokens.SizingSpacing200}
            padding={tokens.SizingSpacing200}
            borderRadius={tokens.SizingCornerL}
            border={`1px solid ${tokens.ColorBorderDefault}`}
            bgcolor={tokens.ColorBackgroundLight}
            height="fit-content"
        >
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                <Stack direction="row" gap={tokens.SizingSpacing150}>
                    <RoundImage
                        src={asset.title_image_thumbnail}
                        alt={tCommon('assetImageAlt')}
                        height="150px"
                        width="50%"
                        objectFit="cover"
                        fallbackImageSrc={[asset?.title_image, '/asset-placeholder.svg'].filter(isNotNullOrUndefined)}
                    />
                    <Box
                        height="150px"
                        width="50%"
                        borderRadius={tokens.SizingCornerL}
                        overflow="hidden"
                        sx={{ '.map-placeholder': { fontSize: '9px !important' } }}
                    >
                        {coordinates && (
                            <Map
                                center={coordinates}
                                apiKey={getMapsApiKey()}
                                language={locale}
                                defaultZoom={8}
                                hasControl={false}
                                options={{ zoomControl: false, scrollwheel: false, fullscreenControl: false }}
                            >
                                <Marker {...coordinates} />
                            </Map>
                        )}
                    </Box>
                </Stack>
                <Stack direction="column" gap={tokens.SizingSpacing075}>
                    <Stack direction="row" justifyContent="space-between" gap={tokens.SizingSpacing075} flexWrap="wrap">
                        <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                            {title}
                        </Typography>
                        {valuation.market_value_with_trend.trend && (
                            <TrendIcon variant="label" trend={valuation.market_value_with_trend.trend} locale={locale} />
                        )}
                    </Stack>
                    <Stack direction="column">
                        <Typography variant="bodyTextMedium">{addressLineOne}</Typography>
                        <Typography variant="bodyTextMedium">{addressLineTwo}</Typography>
                    </Stack>
                </Stack>
                <Divider />
                <ValueRows rows={assetPreviewInfoRows} />
                <Divider />
                <RatingRows rows={ratingPreviewInfoRows} />
            </Stack>
        </Stack>
    )
}
